/**
 * SearchResultCard - Renders a single search result from unified knowledge search.
 *
 * Shows source icon, title, snippet, relevance score, and handles click navigation.
 */

import { useTranslation } from 'react-i18next';
import type { UnifiedSearchResult } from '@services/api/search';

interface SearchResultCardProps {
  result: UnifiedSearchResult;
  onTaskClick?: (taskId: string, boardId: string) => void;
  onBoardClick?: (boardId: string) => void;
  onProjectClick?: (projectId: string) => void;
  onMeetingClick?: (meetingId: string) => void;
  compact?: boolean;
}

export default function SearchResultCard({
  result,
  onTaskClick,
  onBoardClick,
  onProjectClick,
  onMeetingClick,
  compact = false,
}: SearchResultCardProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    const meta = result.metadata as Record<string, string>;
    switch (result.entity_type) {
      case 'task':
        if (onTaskClick && meta.board_id) {
          onTaskClick(result.entity_id, meta.board_id);
        }
        break;
      case 'document':
        // Documents are attached to boards/tasks - navigate to board
        if (onBoardClick && meta.board_id) {
          onBoardClick(meta.board_id);
        }
        break;
      case 'meeting':
        if (onMeetingClick) {
          onMeetingClick(result.entity_id);
        }
        break;
      case 'project':
        if (onProjectClick) {
          onProjectClick(result.entity_id);
        }
        break;
      case 'comment':
        // Navigate to the task the comment belongs to
        if (onTaskClick && meta.task_id && meta.board_id) {
          onTaskClick(meta.task_id, meta.board_id);
        }
        break;
    }
  };

  const getSourceIcon = () => {
    switch (result.entity_type) {
      case 'task':
        return (
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-blue-500`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'document':
        return (
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-500`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'meeting':
        return (
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-green-500`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'comment':
        return (
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-purple-500`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        );
      case 'project':
        return (
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-indigo-500`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        );
      default:
        return (
          <svg className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-neutral-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        );
    }
  };

  const getSourceLabel = () => {
    const labels: Record<string, string> = {
      task: t('search.sourceTask', 'Task'),
      document: t('search.sourceDocument', 'Document'),
      meeting: t('search.sourceMeeting', 'Meeting'),
      comment: t('search.sourceComment', 'Comment'),
      project: t('search.sourceProject', 'Project'),
    };
    return labels[result.entity_type] || result.entity_type;
  };

  const getRelevanceColor = () => {
    if (result.relevance_score >= 0.8) return 'text-green-600 dark:text-green-400';
    if (result.relevance_score >= 0.5) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-neutral-500 dark:text-neutral-400';
  };

  return (
    <div
      onClick={handleClick}
      className={`p-2.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:border-primary-400 dark:hover:border-primary-600 transition-colors cursor-pointer ${compact ? 'text-xs' : 'text-sm'}`}
    >
      {/* Header: icon + type badge + relevance */}
      <div className="flex items-center gap-2 mb-1">
        {getSourceIcon()}
        <span className="inline-block px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 rounded text-[10px] font-medium uppercase tracking-wide">
          {getSourceLabel()}
        </span>
        <span className={`ml-auto text-[10px] font-mono ${getRelevanceColor()}`}>
          {Math.round(result.relevance_score * 100)}%
        </span>
      </div>

      {/* Title */}
      <div className="font-medium text-neutral-900 dark:text-white line-clamp-1">
        {result.title}
      </div>

      {/* Snippet */}
      {result.snippet && (
        <div className="text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2 leading-relaxed">
          {result.snippet}
        </div>
      )}
    </div>
  );
}
